// The ExoWare mark: a forward-leaning E whose middle bar is a detached, glowing core,
// on the red-to-navy badge from the original logo. Same geometry as public/media/icon.svg.
import { useId } from "react";

const E_BODY = "M22 16 H47.5 L45 23.5 H27 V40.5 H47.5 L45 48 H19 V19 Z";
const E_CORE = "29.2,28.6 43,28.6 40.8,35.4 29.2,35.4";
const SLANT = "translate(32 32) skewX(-11) translate(-32 -32)";

export function LogoMark({ className = "size-8", animated = true, framed = true }: {
  className?: string;
  animated?: boolean;
  /** false draws just the E in the brand gradient, for use on dark backgrounds at large sizes. */
  framed?: boolean;
}) {
  const id = useId().replace(/:/g, "");
  return (
    <svg viewBox="0 0 64 64" className={className} aria-hidden>
      <defs>
        <linearGradient id={`bg${id}`} x1="0" y1="1" x2="1" y2="0">
          <stop offset="0" stopColor="#ff5a1a" /><stop offset="0.34" stopColor="#f01f3a" /><stop offset="0.6" stopColor="#7c1638" />
          <stop offset="0.82" stopColor="#1d1848" /><stop offset="1" stopColor="#0d0c26" />
        </linearGradient>
        <radialGradient id={`hot${id}`} cx="0.12" cy="0.95" r="0.75">
          <stop offset="0" stopColor="#ffb36b" stopOpacity="0.55" /><stop offset="1" stopColor="#ffb36b" stopOpacity="0" />
        </radialGradient>
        <linearGradient id={`ink${id}`} x1="0" y1="0" x2={framed ? "0" : "1"} y2="1">
          {framed
            ? <><stop offset="0" stopColor="#ffffff" /><stop offset="1" stopColor="#ffe4dc" /></>
            : <><stop offset="0" stopColor="#ff8a3d" /><stop offset="0.5" stopColor="#ff3d4f" /><stop offset="1" stopColor="#7c1638" /></>}
        </linearGradient>
        <linearGradient id={`sh${id}`} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor="#fff" stopOpacity="0" /><stop offset="0.5" stopColor="#fff" stopOpacity="0.28" /><stop offset="1" stopColor="#fff" stopOpacity="0" />
        </linearGradient>
        <filter id={`gl${id}`} x="-50%" y="-50%" width="200%" height="200%"><feGaussianBlur stdDeviation="2.2" /></filter>
        <clipPath id={`cl${id}`}><rect width="64" height="64" rx="15" /></clipPath>
      </defs>
      {framed && (
        <>
          <g clipPath={`url(#cl${id})`}>
            <rect width="64" height="64" fill={`url(#bg${id})`} />
            <rect width="64" height="64" fill={`url(#hot${id})`} />
            {animated && <rect x="0" y="-10" width="22" height="84" fill={`url(#sh${id})`} className="animate-shine" />}
          </g>
          <rect x="0.6" y="0.6" width="62.8" height="62.8" rx="14.4" fill="none" stroke="#fff" strokeOpacity="0.18" strokeWidth="1.2" />
        </>
      )}
      <g transform={SLANT}>
        <path d={E_BODY} fill={`url(#ink${id})`} />
        <polygon points={E_CORE} fill={framed ? "#ffd2bd" : "#ff3d4f"} filter={`url(#gl${id})`} className={animated ? "animate-core" : undefined} />
        <polygon points={E_CORE} fill={framed ? "#fff" : "#ffe4dc"} />
      </g>
    </svg>
  );
}

/** "Exoware" wordmark; the E carries the brand gradient like the original logo. */
export function Wordmark({ className }: { className?: string }) {
  return (
    <span className={`font-display text-[1.2rem] font-semibold tracking-[0.01em] ${className ?? ""}`}>
      <span className="text-gradient">E</span>xoware
    </span>
  );
}

/** Permanent invite to the ExoWare Discord (#welcome). Created by scripts/discord-branding.ts. */
export const DISCORD_INVITE = "https://discord.gg/aE8ACdJAyq";
